export interface ApiError extends Error {
  type: ErrorType
  message: string
  status?: number
  data?: any
}

export type ErrorType
  = | 'INVALID_ACKNOWLEDGEMENT'
    | 'INVALID_URL_CODE'
    | 'INVALID_VERIFICATION_CODE'
    | 'NETWORK_CONNECTION'
    | 'NO_REGION'
    | 'NO_TOKEN'
    | 'NOT_YET_LINKED'
    | 'PERSISTENCE_OUT_OF_SYNC'
    | 'REGISTRATION_FAILED'
    | 'SSO_LOGIN_FAILED'
    | 'SSO_DESTINATION_NOT_FOUND'
    | 'STORE_NOT_FOUND'
    | 'FORBIDDEN'
    | 'UNKNOWN'

export const API_ERROR_TYPE: Record<string, ErrorType> = {
  INVALID_ACKNOWLEDGEMENT: 'INVALID_ACKNOWLEDGEMENT',
  INVALID_URL_CODE: 'INVALID_URL_CODE',
  INVALID_VERIFICATION_CODE: 'INVALID_VERIFICATION_CODE',
  NETWORK_CONNECTION: 'NETWORK_CONNECTION',
  NO_REGION: 'NO_REGION',
  NO_TOKEN: 'NO_TOKEN',
  NOT_YET_LINKED: 'NOT_YET_LINKED',
  PERSISTENCE_OUT_OF_SYNC: 'PERSISTENCE_OUT_OF_SYNC',
  REGISTRATION_FAILED: 'REGISTRATION_FAILED',
  SSO_LOGIN_FAILED: 'SSO_LOGIN_FAILED',
  SSO_DESTINATION_NOT_FOUND: 'SSO_DESTINATION_NOT_FOUND',
  STORE_NOT_FOUND: 'STORE_NOT_FOUND',
  FORBIDDEN: 'FORBIDDEN',
  UNKNOWN: 'UNKNOWN',
}

/**
 * @description Creates a typed error handler
 */
export function handleApiError(error: unknown): ApiError {
  const apiError = new Error('An unexpected error occurred') as ApiError

  // Preserve status and data if present
  if (error && typeof error === 'object') {
    apiError.status = (error as any).status || (error as any).response?.status
    apiError.data = (error as any).data || (error as any)._data || (error as any).response?._data || (error as any).response?.data
  }

  // Handle Response objects (from fetch API)
  if (error instanceof Response || (error && typeof error === 'object' && 'status' in error)) {
    const response = error as Response & { status?: number }

    if (response.status === 403) {
      apiError.type = API_ERROR_TYPE.FORBIDDEN
      apiError.message = 'Access denied. Your session may have expired.'
      return apiError
    }
  }

  if (error instanceof Error) {
    // Network error check
    if (error.message.includes('Network') || error.message.includes('Failed to fetch')) {
      apiError.type = API_ERROR_TYPE.NETWORK_CONNECTION
      apiError.message = 'Network connection failed. Please check your connection and try again.'
      return apiError
    }

    // Copy the original error message
    apiError.message = error.message
  }

  // Default error
  apiError.type = API_ERROR_TYPE.UNKNOWN
  apiError.message = apiError.message || 'An unexpected error occurred. Please try again later.'
  return apiError
}

/**
 * @description Creates a properly typed API error
 */
export function createApiError(type: ErrorType, message: string): ApiError {
  const error = new Error(message) as ApiError
  error.type = type
  return error
}
